import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import RoomIcon from '@mui/icons-material/Room';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TestRoutesMap from '../../components/TestRoutesMap';
import { api } from '../../services/api';

interface RouteSummary {
  id: number;
  routeName: string;
  description: string;
  centerName: string;
  startLocation: string;
  createdAt: string;
}

interface Coordinate {
  lat: number;
  lng: number;
}

interface Tip {
  id: number;
  commentText: string;
  latitude: number;
  longitude: number;
  tipType: string;
  createdAt: string;
  instructorName: string;
}

interface RouteDetails {
  id: number;
  routeName: string;
  description: string;
  centerName: string;
  startLocation: string;
  coordinatesJson: string;
  comments: Tip[];
}

interface TestRoutesTabProps {
  isInstructor?: boolean;
}

const TestRoutesTab: React.FC<TestRoutesTabProps> = ({ isInstructor = false }) => {
  const [routes, setRoutes] = useState<RouteSummary[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [routeDetails, setRouteDetails] = useState<RouteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getTestRoutes();
      setRoutes(response.data);
      if (response.data.length > 0) {
        setSelectedRouteId(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching test routes:', err);
      setError('Failed to load test routes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRouteDetails = async (id: number) => {
    try {
      setDetailsLoading(true);
      const response = await api.getTestRouteDetails(id);
      setRouteDetails(response.data);
    } catch (err) {
      console.error('Error fetching route details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  useEffect(() => {
    if (selectedRouteId !== null) {
      fetchRouteDetails(selectedRouteId);
    }
  }, [selectedRouteId]);

  const handleAddTip = async (newTip: { commentText: string; latitude: number; longitude: number; tipType: string }) => {
    if (selectedRouteId === null) return;
    try {
      await api.addTestRouteTip(selectedRouteId, newTip);
      // Reload details to display the newly added marker
      fetchRouteDetails(selectedRouteId);
    } catch (err) {
      console.error('Error adding tip:', err);
    }
  };

  const parseCoordinates = (jsonStr: string): Coordinate[] => {
    try {
      return JSON.parse(jsonStr) as Coordinate[];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {routes.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          No local test routes have been added to the system yet. Admin accounts can add routes through the Administrator Dashboard.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Left Panel: Route List */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                    <MapIcon color="primary" /> Test Routes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select a local exam route to explore instructor cautions and tips.
                  </Typography>
                </Box>
                <List disablePadding>
                  {routes.map((route, idx) => (
                    <React.Fragment key={route.id}>
                      <ListItemButton
                        selected={selectedRouteId === route.id}
                        onClick={() => setSelectedRouteId(route.id)}
                        sx={{
                          py: 2,
                          px: 2.5,
                          borderLeft: selectedRouteId === route.id ? '4px solid #1976d2' : '4px solid transparent',
                          bgcolor: selectedRouteId === route.id ? 'action.selected' : 'transparent',
                        }}
                      >
                        <ListItemText
                          primary={route.routeName}
                          secondary={route.centerName}
                          primaryTypographyProps={{ fontWeight: selectedRouteId === route.id ? 'bold' : 'normal' }}
                        />
                      </ListItemButton>
                      {idx < routes.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel: Map & Annotation Details */}
          <Grid size={{ xs: 12, md: 8 }}>
            {detailsLoading && !routeDetails ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress size={30} />
              </Box>
            ) : routeDetails ? (
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Route Header Info */}
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1.5}>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="text.primary">
                          {routeDetails.routeName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <RoomIcon sx={{ fontSize: 16 }} /> Starting Point: {routeDetails.startLocation}
                        </Typography>
                      </Box>
                      <Chip
                        label={routeDetails.centerName}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold', borderRadius: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {routeDetails.description || 'Explore this driving test route to understand common lane markings, tricky turns, and speed limits in this area.'}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Map Display */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Interactive Route Map
                  </Typography>
                  <TestRoutesMap
                    coordinates={parseCoordinates(routeDetails.coordinatesJson)}
                    tips={routeDetails.comments}
                    onAddTip={isInstructor ? handleAddTip : undefined}
                    isInstructor={isInstructor}
                  />
                </Box>

                {/* List of Tips/Alerts */}
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom mb={1.5}>
                    Pinned Instructor Tips ({routeDetails.comments.length})
                  </Typography>
                  {routeDetails.comments.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'action.hover' }}>
                      <HelpOutlineIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No instructor tips have been pinned to this route yet.
                        {isInstructor && ' Click on the map above to add the first caution note!'}
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={2}>
                      {routeDetails.comments.map((comment) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={comment.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              height: '100%',
                              borderColor:
                                comment.tipType === 'Caution'
                                  ? 'rgba(255, 23, 68, 0.2)'
                                  : comment.tipType === 'SpeedLimit'
                                  ? 'rgba(255, 145, 0, 0.2)'
                                  : 'divider',
                              bgcolor:
                                comment.tipType === 'Caution'
                                  ? 'rgba(255, 23, 68, 0.01)'
                                  : 'background.paper',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Chip
                                label={comment.tipType}
                                size="small"
                                color={
                                  comment.tipType === 'Caution'
                                    ? 'error'
                                    : comment.tipType === 'SpeedLimit'
                                    ? 'warning'
                                    : comment.tipType === 'LaneSelection'
                                    ? 'primary'
                                    : 'success'
                                }
                                sx={{ height: 20, fontSize: 10, fontWeight: 'bold', borderRadius: 1.5 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {new Date(comment.createdAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.primary', lineHeight: 1.4 }}>
                              "{comment.commentText}"
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#1976d2' }}>
                                {comment.instructorName[0]}
                              </Avatar>
                              <Typography variant="caption" fontWeight="bold">
                                {comment.instructorName}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                <Typography variant="body1">Select a route on the left to see map details.</Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default TestRoutesTab;
